import { Filter, X } from 'lucide-react';
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { groupStatusOptionsByVariant } from '@/lib/utils';

interface StatusMultiSelectFilterProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const StatusMultiSelectFilter: React.FC<
  StatusMultiSelectFilterProps
> = ({ value, onChange, options, placeholder = 'Filter...' }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Parse comma-separated string to array
  const selectedValues = value ? value.split(',').filter(Boolean) : [];

  // Local state for temporary selections
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);

  // Initialize temp state when popover opens
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setTempSelectedValues(selectedValues);
    }
  };

  const handleOptionToggle = (optionValue: string) => {
    if (tempSelectedValues.includes(optionValue)) {
      // Remove the option if it's already selected
      setTempSelectedValues((prev) => prev.filter((v) => v !== optionValue));
    } else {
      // Add the option if it's not selected
      setTempSelectedValues((prev) => [...prev, optionValue]);
    }
  };

  const handleApply = () => {
    onChange(tempSelectedValues.join(','));
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempSelectedValues([]);
    onChange('');
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const option = options.find((opt) => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    }
    return `${selectedValues.length} Status ausgewählt`;
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="h-4 w-4 text-text-muted hover:text-text-primary cursor-pointer relative"
          title={getDisplayText()}
        >
          <Filter className="h-4 w-4 translate-y-0.5" />
          {selectedValues.length > 0 && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
              {selectedValues.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-2">
          {(() => {
            // Group options by their color variant using the utility function
            const groupedOptions = groupStatusOptionsByVariant(options);

            return Object.entries(groupedOptions).map(
              ([variant, variantOptions]) => (
                <div key={variant} className="space-y-1">
                  {variantOptions.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-2 px-2 py-1 rounded hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleOptionToggle(option.value)}
                    >
                      <Checkbox
                        checked={tempSelectedValues.includes(option.value)}
                        className="pointer-events-none"
                      />
                      <Badge variant={option.variant} className="text-xs">
                        {option.text}
                      </Badge>
                    </div>
                  ))}
                </div>
              ),
            );
          })()}
          <div className="pt-2 border-t space-y-2">
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleApply}
                className="flex-1 text-xs"
              >
                Anwenden
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="flex-1 text-xs"
              >
                Abbrechen
              </Button>
            </div>
            {tempSelectedValues.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="w-full text-xs"
              >
                <X className="h-3 w-3 mr-1" />
                Alle löschen
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
